import { VisitSections } from "./VisitSections";
/**
 * The visit host (O10, CASE-027, F5.md V): a box leads to a box in place, and
 * returns. It reads the address, resolves its last level through the one
 * table, and hands the page's Bento a slot: the visited boxes in the origin
 * box's place, wrapped in the frame. Pages stay unchanged; their acts call
 * `visit(address)` through the kit.
 *
 * History is the address. A visit pushes one entry, so Back is Return; a
 * pasted link or reload lands in the same visit from `?visit=` alone, and
 * Return from it replaces. Next record (CASE-031) replaces its own level.
 *
 * A record left while the visit stays open (onward, Next, a replacing visit)
 * stays mounted under real `hidden` and `inert`, as the origin does, so its
 * drafts and open state are there when the person comes back (O7, CASE-031).
 */
import { useQueryClient } from "@tanstack/react-query";
import { Children, Fragment, isValidElement, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";

import type { Box, VisitAddress } from "@/components/ui/box.schema";
import { MAX_TRAIL, parseLevel, readTrail, searchWithTrail } from "@/lib/visit/address";
import { courseOf, nounOf, planVisit, shellOf, targetOf, type VisitResolution, type VisitTarget } from "@/lib/visit/registry";
import { VISIT_TARGETS } from "@/lib/visit/targets";

import { visitCopy } from "./copy";
import { BentoVisit, type BentoVisitHost, type BentoVisitSlot } from "@/components/ui/box-visit-context";
import { VisitFrame } from "./VisitFrame";
import { hostsVisit } from "./hosts-visit";

/** What a pushed entry remembers, in history state beside the page's own. */
interface VisitMemory {
  /** The origin box the visit took the place of. */
  at: string;
  origin: string;
  /** Visit entries pushed above the origin's, so Return can step Back. */
  depth: number;
  /** Subjects this tab read for earlier levels, by address. */
  labels: Record<string, string>;
  originOpener?: Omit<Opener, "element">;
}

/** Where focus returns: the opener, else its row's act, else its box. Elements can't live in history. */
interface Opener {
  element: HTMLElement | null;
  box: string | null;
  row: string | null;
  scrollY: number;
}

type PendingFocus =
  | { kind: "heading"; until: number; stale?: HTMLElement | null }
  | { kind: "opener"; opener: Opener | undefined; at: string | null; scroll: number | undefined; until: number };

interface FallbackRow {
  address: string;
  owner: string;
  path: string;
}

const MEMORY = "imamVisit";
const FOCUS_WAIT_MS = 3000;
/**
 * Records left behind that a tab keeps mounted while one visit stays open, oldest
 * dropped first. Only records holding no draft count: a draft is never dropped (O7).
 */
const KEPT_LIMIT = 16;

/** Whether a record's boxes hold a draft, as the kit reads it: a Task's, or a row task's on a List (F3, O7). */
function holdsDraft(boxes: readonly Box[]): boolean {
  return boxes.some((box) => box.kind !== "fact" && Boolean(box.draft));
}

function memoryOf(state: unknown): VisitMemory | null {
  const memory = state && typeof state === "object" ? (state as Record<string, unknown>)[MEMORY] : null;
  return memory && typeof memory === "object" ? (memory as VisitMemory) : null;
}

function withMemory(state: unknown, memory: VisitMemory | null): Record<string, unknown> {
  const own = state && typeof state === "object" ? { ...(state as Record<string, unknown>) } : {};
  if (memory) own[MEMORY] = memory;
  else delete own[MEMORY];
  return own;
}

const attr = (value: string) => value.replace(/["\\]/g, "\\$&");

function locate(pending: Extract<PendingFocus, { kind: "opener" }>, root: HTMLElement | null): HTMLElement | null {
  const { opener, at } = pending;
  const element = opener?.element;
  if (element?.isConnected && !element.closest("[hidden],[inert]")) return element;
  const boxId = opener?.box ?? at;
  const box = boxId ? root?.querySelector<HTMLElement>(`[data-box-id="${attr(boxId)}"]`) : null;
  if (!box || box.closest("[hidden],[inert]")) return null;
  // A row drawn at two widths (table and card) is focused where it is shown.
  const rows = opener?.row ? [...box.querySelectorAll(`[data-row="${attr(opener.row)}"]`)] : [];
  const row = rows.find((candidate) => candidate.getClientRects().length > 0) ?? rows[0] ?? null;
  return row?.querySelector<HTMLElement>("[data-act]") ?? box.querySelector<HTMLElement>("[data-face]") ?? null;
}

export function VisitHost({ targets = VISIT_TARGETS, children }: { targets?: readonly VisitTarget[]; children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const rootRef = useRef<HTMLDivElement>(null);
  const headingId = useId();

  const sectionLayouts = useRef(new Map<string, import("@/lib/visit/registry").VisitSection[]>());
  const initialTargets = useRef(targets);
  if (import.meta.env.DEV && initialTargets.current !== targets) throw new Error("A visit host's targets are fixed for its lifetime.");

  const shell = shellOf(location.pathname);
  // A course's own page refuses another course's records before reading them (R33).
  const course = courseOf(location.pathname);
  const trailKey = readTrail(location.search).join(",");
  const trail = useMemo(() => (trailKey ? trailKey.split(",") : []), [trailKey]);
  const plan = useMemo(() => planVisit(trail, targets, shell, course), [trail, targets, shell, course]);
  const memory = memoryOf(location.state);
  const current = trail[trail.length - 1] ?? null;

  // Every registered target's hook at every trail position on every render, in table order, so
  // hook order never changes. Earlier levels resolve too: they name the trail and stay mounted.
  const plans = useMemo(
    () => Array.from({ length: MAX_TRAIL }, (_, index) => (index < trail.length ? planVisit(trail.slice(0, index + 1), targets, shell, course) : null)),
    [trail, targets, shell, course],
  );
  const resolutions: (VisitResolution | null)[] = [];
  for (const levelPlan of plans) {
    let read: VisitResolution | null = null;
    for (const target of targets) {
      if (target.kind !== "registered") continue;
      const drawn = levelPlan?.kind === "visit" && levelPlan.target === target;
      const value = target.useBoxes(drawn ? levelPlan.level : null);
      if (drawn) read = value;
    }
    resolutions.push(read);
  }
  const resolution = trail.length > 0 && trail.length <= MAX_TRAIL ? resolutions[trail.length - 1] : null;

  // Prefer the remembered origin when still drawn. A completed task can remove
  // its section, so a reload must rehome the same visit in a remaining box.
  // Eligibility is the active mode and a stable box identity (#1393 contract
  // 4): a box inside a hidden ancestor (an inactive mode, a hidden frame) is
  // not drawn for the person and never hosts. The suppression the visit itself
  // imposes does not count: Bento hides the origin box itself, and the host
  // root is inert while the visit is open.
  // `at: null` records that the held host is hidden and nothing else can
  // host, so neither it nor the remembered one is used in the meantime.
  const [found, setFound] = useState<{ key: string; at: string | null; origin: string } | null>(null);
  const pending = useRef<PendingFocus | null>(null);
  const held = useRef<string | null>(null);
  const first = trail[0] ?? "";
  const open = plan.kind === "visit" || plan.kind === "refused";
  useLayoutEffect(() => {
    if (!open) { held.current = null; return; }
    const root = rootRef.current;
    if (!root) return;
    const firstTarget = targetOf(targets, first)?.target;
    const hosts = firstTarget?.kind === "registered" ? firstTarget.hosts : [];
    const place = () => {
      const drawn = [...root.querySelectorAll<HTMLElement>("[data-bento-page] [data-box-id]")].filter((box) => !box.closest("[data-visit]") && !box.parentElement?.closest("[hidden]"));
      const box = drawn.find((candidate) => candidate.dataset.boxId === memory?.at)
        ?? hosts.map((id) => drawn.find((candidate) => hostsVisit(candidate.dataset.boxId, id))).find(Boolean) ?? drawn[0];
      if (!box?.dataset.boxId) {
        // A host still drawn but hidden (an inactive mode) is let go. A removed
        // host is kept: the visit still draws its own answer and Return.
        setFound((before) => {
          const held = before?.key === first ? before.at : memory?.at ?? null;
          const hidden = held !== null && [...root.querySelectorAll<HTMLElement>("[data-bento-page] [data-box-id]")]
            .some((candidate) => candidate.dataset.boxId === held && candidate.parentElement?.closest("[hidden]"));
          return hidden ? { key: first, at: null, origin: "" } : before;
        });
        return false;
      }
      const at = box.dataset.boxId;
      // A visit first drawn before its registered host is rehomed there, which
      // redraws its frame: focus it held (or lost to the move) returns to the
      // redrawn heading, never the one being removed (P5).
      const active = document.activeElement;
      if (held.current !== null && held.current !== at && (active === document.body || active?.closest("[data-visit]"))) pending.current = { kind: "heading", until: Date.now() + FOCUS_WAIT_MS, stale: document.getElementById(headingId) };
      held.current = at;
      setFound((before) => (before?.key === first && before.at === at ? before : { key: first, at, origin: box.getAttribute("aria-label") ?? "" }));
      return true;
    };
    place();
    const observer = new MutationObserver(place);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["hidden"] });
    return () => observer.disconnect();
  }, [open, memory?.at, first, targets, headingId]);
  const placed = useMemo(
    () => (found?.key === first ? (found.at === null ? null : { at: found.at, origin: found.origin }) : memory?.at ? { at: memory.at, origin: memory.origin } : null),
    [memory?.at, memory?.origin, found, first],
  );

  // A listed fallback opens its legacy page: the one exception to "no act changes the page path".
  useEffect(() => {
    if (plan.kind === "fallback") navigate(plan.path, { replace: true });
  }, [plan, navigate]);

  const openers = useRef(new Map<number, Opener>());
  const reopen = useRef(new Map<string, string>());
  const closing = useRef<"outside" | null>(null);
  /** A move that first steps Back over pushed entries, then finishes once the address lands there. */
  const landing = useRef<{ length: number; then: () => void } | null>(null);

  // The box sets this visit has read, by address, in the order first read; forgotten when it closes.
  const kept = useRef(new Map<string, readonly Box[]>());
  useLayoutEffect(() => {
    if (!trail.length) {
      kept.current.clear();
      sectionLayouts.current.clear();
      return;
    }
    trail.forEach((address, index) => {
      const read = resolutions[index];
      if (read?.status === "ready") { kept.current.set(address, read.boxes); if (read.sections) sectionLayouts.current.set(address, read.sections); else sectionLayouts.current.delete(address); }
    });
    const clean = [...kept.current].filter(([address, boxes]) => !trail.includes(address) && !holdsDraft(boxes)).map(([address]) => address);
    for (const address of clean.slice(0, Math.max(0, clean.length - KEPT_LIMIT))) { kept.current.delete(address); sectionLayouts.current.delete(address); }
  });

  const latest = useRef({ trail, location, memory, resolution, placed, current });
  /** The origin box of the visit last drawn: Return from a pasted link focuses it, the opener being unknown. */
  const lastAt = useRef<string | null>(null);
  const originOpener = useRef<Omit<Opener, "element"> | undefined>(undefined);
  useLayoutEffect(() => {
    latest.current = { trail, location, memory, resolution, placed, current };
    if (placed) lastAt.current = placed.at;
    if (memory?.originOpener) originOpener.current = memory.originOpener;
  });

  /**
   * Steps Back over `pop` pushed entries, then runs `then` where they land, so no
   * entry is left behind the result that Back could reopen (O10, Back is Return).
   */
  const land = useCallback(
    (pop: number, then: () => void) => {
      if (pop <= 0) {
        then();
        return;
      }
      landing.current = { length: latest.current.trail.length - pop, then };
      navigate(-pop);
    },
    [navigate],
  );

  /** Replaces the landed entry with a trail; a pasted or reloaded entry is the only one left to replace. */
  const settle = useCallback(
    (to: readonly string[]) => {
      const { location, memory } = latest.current;
      navigate(
        { pathname: location.pathname, search: searchWithTrail(location.search, to), hash: location.hash },
        { replace: true, state: withMemory(location.state, to.length && memory ? { ...memory, depth: 0 } : null) },
      );
    },
    [navigate],
  );

  const step = useCallback(
    (to: number) => {
      const { trail, memory } = latest.current;
      const steps = trail.length - to;
      if (steps <= 0) return;
      const depth = memory?.depth ?? 0;
      if (depth >= steps) navigate(-steps);
      else land(depth, () => settle(trail.slice(0, to)));
    },
    [navigate, land, settle],
  );

  const onOutside = useCallback(() => {
    const { trail, memory } = latest.current;
    closing.current = "outside";
    const depth = memory?.depth ?? 0;
    if (depth >= trail.length) navigate(-depth);
    else land(depth, () => settle([]));
  }, [navigate, land, settle]);

  const visit = useCallback<BentoVisitHost["visit"]>(
    (address, from) => {
      const { trail, location, memory, resolution, placed, current } = latest.current;
      const inside = resolution?.status === "ready" && resolution.boxes.some((box) => box.id === from.box);
      const sibling = inside && from.next === true;
      const next = !inside ? [address] : sibling ? [...trail.slice(0, -1), address] : [...trail, address];
      const target = planVisit(next, targets, shellOf(location.pathname), courseOf(location.pathname));
      if (target.kind === "fallback") {
        navigate(target.path);
        return;
      }
      // A visit no act opened (a restored address) has no opener element: Return finds its row instead of the page body.
      const active = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
      const opener: Opener = {
        element: active,
        box: active?.closest<HTMLElement>("[data-box-id]")?.dataset.boxId ?? from.box,
        row: active?.closest<HTMLElement>("[data-row]")?.dataset.row ?? from.row ?? null,
        scrollY: window.scrollY,
      };
      let remembered: VisitMemory;
      let replace: boolean;
      if (sibling) {
        // The origin row of the record last visited takes the opener's place (CASE-031).
        const before = openers.current.get(trail.length);
        openers.current.set(trail.length, { element: null, box: before?.box ?? null, row: parseLevel(address)?.id ?? null, scrollY: before?.scrollY ?? opener.scrollY });
        remembered = { ...(memory ?? { at: placed?.at ?? from.box, origin: placed?.origin ?? "", depth: 0, labels: {} }), originOpener: { box: before?.box ?? null, row: parseLevel(address)?.id ?? null, scrollY: before?.scrollY ?? opener.scrollY } };
        replace = true;
      } else if (inside && current) {
        openers.current.set(next.length, opener);
        reopen.current.set(current, opener.box ?? from.box);
        const subject = resolution?.status === "ready" ? resolution.subject : "";
        const base = memory ?? { at: placed?.at ?? from.box, origin: placed?.origin ?? "", depth: 0, labels: {} };
        remembered = { ...base, depth: base.depth + 1, labels: subject ? { ...base.labels, [current]: subject } : base.labels };
        replace = false;
      } else {
        // A new visit. From a box outside an open one, it replaces that visit: one visit per page.
        // It steps Back to the visit's first entry, which stays open so nothing it kept unmounts,
        // and replaces that entry, so Back from the new visit is the page, never the old trail.
        const origin = rootRef.current?.querySelector(`[data-box-id="${attr(from.box)}"]`)?.getAttribute("aria-label") ?? "";
        const start = () => {
          const { trail, location, memory } = latest.current;
          openers.current = new Map([[1, opener]]);
          reopen.current.delete(address);
          const depth = trail.length ? (memory?.depth ?? 0) : (memory?.depth ?? 0) + 1;
          navigate(
            { pathname: location.pathname, search: searchWithTrail(location.search, [address]), hash: location.hash },
            { replace: trail.length > 0, state: withMemory(location.state, { at: from.box, origin, depth, labels: {}, originOpener: { box: opener.box, row: opener.row, scrollY: opener.scrollY } }) },
          );
        };
        const pop = Math.min(memory?.depth ?? 0, trail.length - 1);
        if (pop > 0) closing.current = "outside";
        land(pop, start);
        return;
      }
      navigate(
        { pathname: location.pathname, search: searchWithTrail(location.search, next), hash: location.hash },
        { replace, state: withMemory(location.state, remembered) },
      );
    },
    [navigate, targets, land],
  );

  // Entry focuses the heading (P5); Return focuses the opener and re-reads what it returns to.
  const before = useRef<readonly string[]>([]);
  useEffect(() => {
    const prior = before.current;
    before.current = trail;
    if (prior.join(",") === trail.join(",")) return;
    // An outside act's move may land twice (Back, then replace); it moves no focus until it ends.
    const outside = closing.current === "outside";
    if (!landing.current) closing.current = null;
    const until = Date.now() + FOCUS_WAIT_MS;
    if (trail.length >= prior.length) {
      pending.current = { kind: "heading", until };
      return;
    }
    // A read still in flight began before Return; a first read with nothing shown
    // yet is joined, not restarted, by invalidation, so stop it and read again.
    void queryClient.cancelQueries({ type: "active", predicate: (query) => query.state.data === undefined && query.state.fetchStatus === "fetching" })
      .then(() => queryClient.invalidateQueries({ refetchType: "active" }));
    const opener = openers.current.get(trail.length + 1) ?? (trail.length === 0 && originOpener.current ? { ...originOpener.current, element: null } : undefined);
    for (const level of [...openers.current.keys()]) if (level > trail.length) openers.current.delete(level);
    pending.current = outside ? null : trail.length > 0 && !opener ? { kind: "heading", until } : { kind: "opener", opener, at: lastAt.current, scroll: trail.length === 0 ? opener?.scrollY : undefined, until };
  }, [trail, queryClient]);

  useEffect(() => {
    const want = landing.current;
    if (!want || trail.length > want.length) return;
    landing.current = null;
    if (trail.length === want.length) want.then();
  }, [trail]);

  useEffect(() => {
    const move = () => {
      const want = pending.current;
      if (!want) return true;
      if (Date.now() > want.until) { pending.current = null; return true; }
      if (want.kind === "heading") {
        const heading = document.getElementById(headingId);
        if (!heading || heading === want.stale || heading.closest("[hidden],[inert]")) return false;
        const preferred = resolution && "entryFocus" in resolution ? resolution.entryFocus : undefined;
        const target = preferred ? heading.closest("[data-visit]")?.querySelector<HTMLElement>(preferred) : heading;
        if (!target) return false;
        target.focus(); pending.current = null; return true;
      }
      if (want.scroll !== undefined) {
        window.scrollTo({ top: want.scroll });
        pending.current = { ...want, scroll: undefined };
      }
      const activeFrame = document.getElementById(headingId)?.closest<HTMLElement>("[data-visit]") ?? null;
      const target = locate(want, activeFrame) ?? locate(want, rootRef.current);
      if (!target) return false;
      target.focus({ preventScroll: true }); pending.current = null; return true;
    };
    if (move()) return;
    // Radix portals mount after their owning render. Observe their actual DOM
    // arrival, rather than claiming focus from an inline-only effect.
    const observer = new MutationObserver(() => { if (move()) observer.disconnect(); });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["hidden", "inert"] });
    return () => observer.disconnect();
  });

  const refused = plan.kind === "refused" || resolution?.status === "refused";
  const ready = resolution?.status === "ready" ? resolution : null;
  const heading = refused ? visitCopy.refusedHeading : (ready?.subject ?? (current ? (nounOf(targets, current) ?? "") : ""));
  // Each earlier level is named by its own subject, the one its heading gave (C1).
  const names = [
    placed?.origin ?? "",
    ...trail.slice(0, -1).map((raw, index) => {
      const read = resolutions[index];
      return (read?.status === "ready" ? read.subject : null) ?? memory?.labels[raw] ?? nounOf(targets, raw) ?? "";
    }),
  ];

  // The kit draws every box the slot carries; the shown record's come first, so intent lands there.
  // A kept record's boxes keep their ids and lose their candidates: hidden, they never rank (P2).
  const groupOf = new Map<string, string>();
  const boxes: Box[] = [];
  const carry = (address: string, set: readonly Box[], shown: boolean) => {
    for (const box of set) {
      if (groupOf.has(box.id)) continue;
      groupOf.set(box.id, address);
      boxes.push(shown ? box : ({ ...box, candidate: undefined } as Box));
    }
  };
  const showing = current && !refused && ready ? current : null;
  if (showing && ready) carry(showing, ready.boxes, true);
  const groups = [...new Set([...kept.current.keys(), ...trail])];
  for (const address of groups) {
    if (address === showing) continue;
    const index = trail.indexOf(address);
    const live = index >= 0 ? resolutions[index] : null;
    carry(address, live?.status === "ready" ? live.boxes : (kept.current.get(address) ?? []), false);
  }

  const reopened = current ? reopen.current.get(current) : undefined;
  const slot: BentoVisitSlot | null =
    open && placed && current
      ? {
          address: current as VisitAddress,
          at: placed.at,
          boxes,
          opens: [...new Set([...(ready?.opens ?? []), ...(reopened ? [reopened] : [])])],
          render: (visited) => {
            const drawn = isValidElement<{ children?: ReactNode }>(visited) ? Children.toArray(visited.props.children) : [];
            const byAddress = new Map<string, ReactNode[]>();
            for (const element of drawn) {
              // React owns element keys and may escape them. Box ids belong to
              // the host and remain opaque, including colons and equals signs.
              const id = isValidElement<{ box?: Box; "data-box-id"?: string }>(element)
                ? element.props.box?.id ?? element.props["data-box-id"]
                : undefined;
              const address = id === undefined ? undefined : groupOf.get(id);
              if (address) byAddress.set(address, [...(byAddress.get(address) ?? []), element]);
            }
            return (
              <VisitFrame
                trail={trail}
                names={names}
                heading={heading}
                headingId={headingId}
                refused={refused}
                busy={!refused && !ready}
                onStep={to => { if (ready?.beforeLeave) void ready.beforeLeave().then(() => step(to)); else step(to); }}
                activeBoxIds={ready?.boxes.map((box) => box.id)}
                dockFooter={ready?.boxes.some((box) => box.kind === "task" && box.footer === "host")}
              >
                {groups.map((address) => {
                  const live = resolutions[trail.indexOf(address)];
                  const sections = live?.status === "ready" ? live.sections : sectionLayouts.current.get(address);
                  const nodes = byAddress.get(address) ?? [];
                  // A kept portal group must not retain flex display, which overrides native hidden.
                  return <div key={address} hidden={address !== showing} inert={address !== showing} data-visit-kept={address !== showing ? "" : undefined} className={address === showing && !sections ? "bento" : undefined}>
                    {sections ? <VisitSections sections={sections} nodes={nodes} opens={live?.status === "ready" ? live.opens : undefined} /> : nodes}
                  </div>;
                })}
              </VisitFrame>
            );
          },
          onOutside,
        }
      : null;

  // The judge's registry: the fallbacks the acts drawn here can reach, each a named deviation.
  const [fallbacks, setFallbacks] = useState<FallbackRow[]>([]);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let frame = 0;
    const scan = () => {
      frame = 0;
      const rows = new Map<string, FallbackRow>();
      for (const act of root.querySelectorAll<HTMLElement>("[data-act=visit][data-visit-to]")) {
        const address = act.dataset.visitTo ?? "";
        const reach = planVisit([address], targets, shell, course);
        if (reach.kind === "fallback") rows.set(address, { address, owner: reach.target.owner, path: reach.path });
      }
      const next = [...rows.values()];
      setFallbacks((prior) => (JSON.stringify(prior) === JSON.stringify(next) ? prior : next));
    };
    scan();
    const observer = new MutationObserver(() => {
      if (!frame) frame = requestAnimationFrame(scan);
    });
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-visit-to"] });
    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [targets, shell, course]);

  // The slot is drawn afresh each render, from live reads and kept records, so the host is too.
  const host: BentoVisitHost = { visit, slot };

  return (
    <BentoVisit.Provider value={host}>
      <div ref={rootRef} data-visit-host="" inert={Boolean(slot) || undefined} className="contents">
        {children}
        {resolutions.map((read, index) => read?.status === "ready" && read.controller
          ? <Fragment key={trail[index]}>{read.controller}</Fragment> : null)}
      </div>
      {fallbacks.length > 0 && (
        <script type="application/json" data-visit-fallbacks="">
          {JSON.stringify(fallbacks)}
        </script>
      )}
    </BentoVisit.Provider>
  );
}

